import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  fetchExternalMonthlyReport,
  fetchInternalMonthlyAnalysis,
  fetchMonthlyReportCommercialTerms,
  fetchMonthlyReportArtifacts,
  fetchMonthlyReportPrecheck,
  fetchMonthlyReportProfiles,
  fetchValuationComparison,
  generateMonthlyReportArtifact,
  lockMonthlyReportCommercialTerms,
  lockMonthlyReportProfile,
  supersedeMonthlyReportCommercialTerms,
  supersedeMonthlyReportProfile,
} from './api';
import type {
  ExternalMonthlyReportDto,
  InternalMonthlyAnalysisDto,
  MonthlyReportArtifactListDto,
  MonthlyReportCommercialTermsDto,
  MonthlyReportGenerateDto,
  MonthlyReportGenerateRequest,
  MonthlyReportPagedList,
  MonthlyReportPrecheckDto,
  MonthlyReportProfileDto,
  MonthlyReportVariant,
  ValuationComparisonDto,
} from './types';

const KEY = 'monthly-reports' as const;

/**
 * External Monthly Report (price-free). `enabled` is gated by the caller's
 * permission so we never fire the request when the section is hidden.
 */
export const useExternalMonthlyReport = (batchId: string | undefined, enabled = true) => {
  return useQuery<ExternalMonthlyReportDto, ApiClientError>({
    queryKey: [KEY, 'external', batchId],
    queryFn: () => fetchExternalMonthlyReport(batchId!),
    enabled: !!batchId && enabled,
  });
};

/** Internal Monthly Analysis (full financials). Permission-gated via `enabled`. */
export const useInternalMonthlyAnalysis = (batchId: string | undefined, enabled = true) => {
  return useQuery<InternalMonthlyAnalysisDto, ApiClientError>({
    queryKey: [KEY, 'internal', batchId],
    queryFn: () => fetchInternalMonthlyAnalysis(batchId!),
    enabled: !!batchId && enabled,
  });
};

/** FR-011 valuation comparison (money — same permission as the internal analysis). */
export const useValuationComparison = (batchId: string | undefined, enabled = true) => {
  return useQuery<ValuationComparisonDto, ApiClientError>({
    queryKey: [KEY, 'comparison', batchId],
    queryFn: () => fetchValuationComparison(batchId!),
    enabled: !!batchId && enabled,
  });
};

export const useMonthlyReportPrecheck = (
  batchId: string | undefined,
  variant: MonthlyReportVariant,
  locale: 'ja' | 'zh-TW' | 'en',
  recipientCompanyId?: string | null,
  enabled = true,
) => {
  return useQuery<MonthlyReportPrecheckDto, ApiClientError>({
    queryKey: [KEY, 'artifact-precheck', batchId, variant, locale, recipientCompanyId ?? null],
    queryFn: () => fetchMonthlyReportPrecheck(batchId!, variant, locale, recipientCompanyId),
    enabled: !!batchId && enabled,
  });
};

export const useMonthlyReportArtifacts = (
  batchId: string | undefined,
  variant: MonthlyReportVariant,
  page: number,
  perPage: number,
  recipientCompanyId?: string | null,
  enabled = true,
) => {
  return useQuery<MonthlyReportArtifactListDto, ApiClientError>({
    queryKey: [KEY, 'artifacts', batchId, variant, page, perPage, recipientCompanyId ?? null],
    queryFn: () =>
      fetchMonthlyReportArtifacts(batchId!, variant, page, perPage, recipientCompanyId),
    enabled: !!batchId && enabled,
  });
};

export const useGenerateMonthlyReportArtifact = (batchId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<MonthlyReportGenerateDto, ApiClientError, MonthlyReportGenerateRequest>({
    mutationFn: (request) => generateMonthlyReportArtifact(batchId!, request),
    onSuccess: (_artifact, request) => {
      void queryClient.invalidateQueries({ queryKey: [KEY, 'artifact-precheck', batchId] });
      void queryClient.invalidateQueries({
        queryKey: [KEY, 'artifacts', batchId, request.variant],
      });
    },
  });
};

export const useMonthlyReportProfiles = (
  batchId: string | undefined,
  variant: MonthlyReportVariant,
  page: number,
  perPage: number,
  enabled = true,
) => {
  return useQuery<MonthlyReportPagedList<MonthlyReportProfileDto>, ApiClientError>({
    queryKey: [KEY, 'settings', 'profiles', batchId, variant, page, perPage],
    queryFn: () => fetchMonthlyReportProfiles(batchId!, variant, page, perPage),
    enabled: !!batchId && enabled,
  });
};

export const useMonthlyReportCommercialTerms = (
  batchId: string | undefined,
  variant: MonthlyReportVariant,
  page: number,
  perPage: number,
  enabled = true,
) => {
  return useQuery<MonthlyReportPagedList<MonthlyReportCommercialTermsDto>, ApiClientError>({
    queryKey: [KEY, 'settings', 'terms', batchId, variant, page, perPage],
    queryFn: () => fetchMonthlyReportCommercialTerms(batchId!, variant, page, perPage),
    enabled: !!batchId && enabled,
  });
};

export const useLockMonthlyReportProfile = (batchId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<MonthlyReportProfileDto, ApiClientError, string>({
    mutationFn: (profileId) => lockMonthlyReportProfile(batchId!, profileId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY, 'settings', 'profiles', batchId] });
      void queryClient.invalidateQueries({ queryKey: [KEY, 'artifact-precheck', batchId] });
      void queryClient.invalidateQueries({ queryKey: [KEY, 'artifacts', batchId] });
    },
  });
};

export const useSupersedeMonthlyReportProfile = (batchId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<
    MonthlyReportProfileDto,
    ApiClientError,
    { profileId: string; reason?: string | null }
  >({
    mutationFn: ({ profileId, reason }) =>
      supersedeMonthlyReportProfile(batchId!, profileId, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY, 'settings', 'profiles', batchId] });
      void queryClient.invalidateQueries({ queryKey: [KEY, 'artifact-precheck', batchId] });
      void queryClient.invalidateQueries({ queryKey: [KEY, 'artifacts', batchId] });
    },
  });
};

export const useLockMonthlyReportCommercialTerms = (batchId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<MonthlyReportCommercialTermsDto, ApiClientError, string>({
    mutationFn: (termSetId) => lockMonthlyReportCommercialTerms(batchId!, termSetId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY, 'settings', 'terms', batchId] });
      void queryClient.invalidateQueries({ queryKey: [KEY, 'artifact-precheck', batchId] });
      void queryClient.invalidateQueries({ queryKey: [KEY, 'artifacts', batchId] });
    },
  });
};

export const useSupersedeMonthlyReportCommercialTerms = (batchId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<
    MonthlyReportCommercialTermsDto,
    ApiClientError,
    { termSetId: string; reason?: string | null }
  >({
    mutationFn: ({ termSetId, reason }) =>
      supersedeMonthlyReportCommercialTerms(batchId!, termSetId, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY, 'settings', 'terms', batchId] });
      void queryClient.invalidateQueries({ queryKey: [KEY, 'artifact-precheck', batchId] });
      void queryClient.invalidateQueries({ queryKey: [KEY, 'artifacts', batchId] });
    },
  });
};
